import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { AttendanceRecordDto } from '@hrms/shared';
import { patch, toApiError } from '@/lib/api-client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';


interface DeclinePunchDialogProps {
  record: AttendanceRecordDto | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Refusing a punch, or putting one back.
 *
 * Framed around the consequence rather than the mechanism: declining costs the
 * employee a day's pay, so the dialog says that in words before the button is
 * pressed rather than leaving it to be discovered on a payslip.
 */
export function DeclinePunchDialog({ record, onOpenChange }: DeclinePunchDialogProps) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isDeclined = Boolean(record?.declinedAt);

  const review = useMutation({
    mutationFn: (body: { action: 'DECLINE'; reason: string } | { action: 'RESTORE' }) =>
      patch<AttendanceRecordDto>(`/attendance/records/${record!.id}/review`, body),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
      void queryClient.invalidateQueries({ queryKey: ['on-duty'] });
      toast.success(
        updated.declinedAt
          ? 'Punch declined — the day will be counted as an absence'
          : 'Punch restored — the day is credited again',
      );
      setReason('');
      setError(null);
      onOpenChange(false);
    },
    onError: (failure) => setError(toApiError(failure).message),
  });

  return (
    <Dialog open={record !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isDeclined ? 'Restore this punch?' : 'Decline this punch?'}
          </DialogTitle>
          <DialogDescription>
            {record?.employeeName} — {record?.date}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isDeclined ? (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border bg-muted/40 p-3">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Declined by {record?.declinedByName ?? 'someone'}
              </p>
              <p className="mt-1">{record?.declineReason}</p>
            </div>
            <p className="text-muted-foreground">
              Restoring credits the day again. The recorded times were never changed, so they
              come back exactly as they were punched.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <Alert variant="warning">
              <TriangleAlert aria-hidden="true" />
              <AlertDescription>
                The day will be counted as an <strong>absence</strong> and the employee will not
                be paid for it. The punched times are kept, so this can be undone.
              </AlertDescription>
            </Alert>

            <FormField
              label="Reason"
              required
              hint="Seen by anyone reviewing this record, and by whoever handles an appeal."
            >
              {(props) => (
                <textarea
                  {...props}
                  rows={3}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="e.g. Photo shows the employee was not at the assigned site."
                  className="w-full rounded-md border border-input bg-background p-3 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                />
              )}
            </FormField>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={review.isPending}>
            Cancel
          </Button>
          {isDeclined ? (
            <Button loading={review.isPending} onClick={() => review.mutate({ action: 'RESTORE' })}>
              Restore punch
            </Button>
          ) : (
            <Button
              variant="destructive"
              loading={review.isPending}
              disabled={reason.trim().length === 0}
              onClick={() => review.mutate({ action: 'DECLINE', reason: reason.trim() })}
            >
              Decline punch
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
